/**
 * Report: Newsletters by Partner.
 *
 * Read-only — nothing here edits an update; the one way out to writing is the New
 * Update button, which opens the form with the unit chosen. Pick a partner from a
 * searchable dropdown; the sidebar is the unit's own profile card, the one the
 * partner unit record leads with (PartnerProfileCard), and the body lists every
 * newsletter collapsed to a single line (date first) that expands in place.
 * "Expand all" opens every row's full field set at once.
 *
 * Each row also links out to the newsletter's own page, which is where the
 * images, the change history and the neighbouring newsletters live.
 *
 * The filter panel holds two kinds of control, and each field says which it is: country
 * and initial year narrow the strip of partner units — they help you find who to read —
 * while the date range and text search filter the updates of whoever is open.
 */
import { useState, useMemo, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi, useLookups } from '../lib/api'
import { useCanBrowse } from '../lib/viewmode'
import { PartnerSelect, CountrySelect } from '../components/Select'
import { DateRangeField } from '../components/DateField'
import { RichTextView, PrayerText } from '../components/RichText'
import { PartnerProfileCard } from '../components/PartnerProfileCard'
import {
  Card, Field, Badge, Switch, Empty, Loading, ErrorState, Notice, Filters, FilterActions, countFilters,
  fmtInt, fmtDate, downloadCsv, SearchInput,
} from '../components/ui'
import { Icon } from '../components/Icon'
import { SortBar } from '../components/DataTable'
import { useCode } from '../lib/terms'

/** S4CA-007 sorts before S4CA-012 either way, but the codes are only padded by luck. */
const codeNumber = (code) => Number(String(code).replace(/\D/g, '')) || 0

const text = (v) => String(v ?? '').toLowerCase()

/**
 * The fields the tags can be ordered by, matching the bar over the partner directory's
 * cards field for field, so the same six words mean the same six things on both pages.
 * `desc` is the direction a field opens in — most updates and most recent first, since
 * that is the question those two are asked.
 */
const TAG_SORTS = [
  { key: 's4ca_code', label: 'Code', order: 'in code order', of: (p) => codeNumber(p.s4ca_code) },
  { key: 'display_name', label: 'Name', order: 'by name', of: (p) => text(p.display_name) },
  { key: 'country_name', label: 'Country', order: 'by country', of: (p) => text(p.country_name) },
  { key: 'initial_year', label: 'Since', order: 'by the year they came on', of: (p) => p.initial_year },
  {
    key: 'newsletter_count', label: 'Updates', order: 'by how much they have sent',
    of: (p) => p.newsletter_count ?? 0, desc: true,
  },
  {
    key: 'last_newsletter', label: 'Latest', order: 'by when they last wrote',
    of: (p) => p.last_newsletter, desc: true,
  },
]

/**
 * Every current partner unit as a tag, so the report can be moved between them without
 * reopening the picker. Alumni are left off, as they are on every other page. Units with
 * nothing on file stay grey rather than being dropped: the picker above already hides
 * them, and their absence here would read as a gap in the numbering rather than as
 * "this one has never written".
 *
 * Code order to begin with, because that is how the codes are quoted elsewhere; the bar
 * reorders the same set, and the country and year filters above narrow it.
 *
 * The strip folds away once a unit is picked. Two hundred and fifty-six tags are a
 * screenful, and the moment one is chosen they are between the reader and the thing they
 * chose — but they are also how the next one is chosen, so the strip is not replaced by
 * a picker, only shut. Opening it again is one press on its heading.
 */
function PartnerTags({ selected, onPick, countryIds, yearFrom, yearTo }) {
  const code = useCode()
  const { data, isLoading } = useLookups()
  const [sort, setSort] = useState('s4ca_code')
  const [dir, setDir] = useState('asc')

  // Shut when something is selected, open when nothing is — and a press overrides that
  // until the selection next changes, so opening the strip to browse keeps it open, and
  // picking from it shuts it again. Adjusted while rendering rather than in an effect,
  // the way the sidebar's folding groups are, so it is never painted in the wrong state
  // for a frame.
  const [open, setOpen] = useState(!selected)
  const [seen, setSeen] = useState(selected)
  if (seen !== selected) {
    setSeen(selected)
    setOpen(!selected)
  }

  const onSort = (key) => {
    if (sort === key) setDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    else {
      setSort(key)
      setDir(TAG_SORTS.find((s) => s.key === key)?.desc ? 'desc' : 'asc')
    }
  }

  /*
   * The strip is partner units, and only partner units.
   *
   * This report is titled "Updates by Partner Unit" and is a reading of the partner
   * corps — so a training school or a project among the tags is a record the report is
   * not about, in a strip somebody scans by eye expecting one kind of thing. Their
   * updates are not hidden anywhere: the Updates page lists every kind and has a
   * "Written by" filter, and each record's own page carries its updates.
   *
   * The exception is a record that is *already open*, which is the same rule the country
   * and year filters below already follow: whatever is being read stays on the strip,
   * because a strip that dropped the record the report is showing would leave the body
   * pointing at no tag. That is what a link arriving from elsewhere with a training
   * school's id lands on — it opens, it is readable, and it is the only non-staff tag
   * that can appear.
   */
  const current = useMemo(
    () =>
      (data?.partners ?? []).filter(
        (p) => p.status !== 'alumni' && ((p.fund_type ?? 'staff') === 'staff' || p.id === selected)
      ),
    [data, selected]
  )

  const tags = useMemo(() => {
    const field = TAG_SORTS.find((s) => s.key === sort) ?? TAG_SORTS[0]
    const sign = dir === 'desc' ? -1 : 1
    const from = Number(yearFrom) || null
    const to = Number(yearTo) || null
    return current
      .filter((p) => {
        // Whatever is being read stays on the strip. A filter that took the selected
        // unit away would leave the report below it with no tag to point at.
        if (p.id === selected) return true
        if (countryIds.length && !countryIds.includes(p.country_id)) return false
        // A unit with no year on file cannot be said to fall inside a year range, so
        // asking for one leaves it out.
        if ((from || to) && !p.initial_year) return false
        if (from && p.initial_year < from) return false
        if (to && p.initial_year > to) return false
        return true
      })
      .sort((a, b) => {
        const x = field.of(a)
        const y = field.of(b)
        // A unit with no year on file, or nothing ever sent, is not an answer to
        // "earliest" any more than to "latest" — those sit at the end either way,
        // in code order among themselves.
        if (x == null || x === '') return y == null || y === '' ? 0 : 1
        if (y == null || y === '') return -1
        if (x < y) return -sign
        if (x > y) return sign
        return codeNumber(a.s4ca_code) - codeNumber(b.s4ca_code)
      })
  }, [current, sort, dir, selected, countryIds, yearFrom, yearTo])

  const withUpdates = tags.filter((p) => p.newsletter_count > 0).length
  const order = (TAG_SORTS.find((s) => s.key === sort) ?? TAG_SORTS[0]).order
  const narrowed = tags.length !== current.length
  const chosen = tags.find((p) => p.id === selected)

  return (
    <Card
      open={open}
      onToggle={() => setOpen((v) => !v)}
      title="Current Partner Units"
      sub={
        // Shut, the heading is all there is to go on, so it says what was picked and
        // that this is the way to pick another. Open, it describes the strip below it.
        !open
          ? `${chosen ? `${code(chosen.s4ca_code)} ${chosen.display_name} selected. ` : ''}` +
            `Open to choose another of the ${fmtInt(tags.length)}.`
          : tags.length
            ? `${narrowed ? `${fmtInt(tags.length)} of ${fmtInt(current.length)}` : fmtInt(tags.length)} ` +
              `${order}, alumni left out — ${fmtInt(withUpdates)} with updates on file. ` +
              'The gray ones have never sent anything.'
            : null
      }
      actions={
        // The sort bar orders the strip, so it goes away with it rather than sitting
        // over a shut panel offering to reorder nothing.
        open && tags.length ? (
          <SortBar options={TAG_SORTS} sort={sort} dir={dir} onSort={onSort} />
        ) : null
      }
    >
      {isLoading ? (
        <Loading label="Loading partner units…" />
      ) : !tags.length ? (
        <Empty
          title={
            current.length
              ? 'No partner unit matches the country or year filters'
              : 'No current partner units'
          }
        />
      ) : (
        <div className="partner-tags">
          {tags.map((p) => {
            const on = p.id === selected
            const has = p.newsletter_count > 0
            return (
              <button
                key={p.id}
                type="button"
                className={`partner-tag${on ? ' on' : ''}${has ? '' : ' quiet'}`}
                aria-pressed={on}
                title={[
                  p.country_name,
                  has
                    ? `${fmtInt(p.newsletter_count)} update${p.newsletter_count === 1 ? '' : 's'}`
                    : 'No updates on file',
                ]
                  .filter(Boolean)
                  .join(' · ')}
                onClick={() => onPick(on ? null : p.id)}
              >
                <span className="partner-tag-code">{code(p.s4ca_code)}</span>
                {/* Placeholder records for codes missing from the registry are named
                    after the code itself, so the tag would otherwise say it twice. */}
                {p.display_name && p.display_name !== p.s4ca_code ? (
                  <span className="truncate">{p.display_name}</span>
                ) : null}
              </button>
            )
          })}
        </div>
      )}
    </Card>
  )
}

function NewsletterRow({ n, open, onToggle, expandFields }) {
  return (
    <div className={`nl-row${open ? ' open' : ''}`}>
      {/* The row is a button plus a sibling link — the link cannot live inside the
          expand/collapse button, so the two share a flex line. */}
      <div className="nl-row-main">
        <button
          className="nl-summary"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`nl-detail-${n.id}`}
        >
          <span className="nl-caret" aria-hidden="true">
            <Icon name="chevron" size={16} />
          </span>
          {/* date is deliberately the first column */}
          <span className="nl-date">{n.published_on}</span>
          <span className="nl-preview">{n.snippet || <span className="subtle">No text</span>}</span>
          <span className="nl-flags">
            {n.highlighted_need ? (
              <Badge tone="warn" title="Highlighted need">
                <Icon name="star" size={12} />
              </Badge>
            ) : null}
            {n.has_prayer_request ? (
              <Badge tone="accent" title="Has prayer requests">
                <Icon name="prayer" size={12} />
              </Badge>
            ) : null}
            {n.country_mismatch ? (
              <Badge title={`Written from ${n.country_name}, which differs from the partner unit's country`}>
                {n.country_name}
              </Badge>
            ) : null}
            <span className="tiny subtle nums">{fmtInt(n.word_count)}w</span>
          </span>
        </button>
        <Link
          className="btn btn-sm nl-open"
          to={`/newsletters/${n.id}`}
          title={`Open the full record for ${fmtDate(n.published_on)} — images, history and every field`}
        >
          View →
        </Link>
      </div>

      {open && (
        <div className="nl-detail" id={`nl-detail-${n.id}`}>
          <div className="nl-detail-section">
            <div className="nl-detail-label">
              Update
              <span className="subtle" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                {fmtDate(n.published_on)} · {fmtInt(n.word_count)} words
              </span>
            </div>
            <RichTextView html={n.body_html} serif />
          </div>

          {(expandFields || n.has_prayer_request) && (
            <div className="nl-detail-section">
              <div className="nl-detail-label">Prayer Requests</div>
              {n.has_prayer_request ? (
                <div className="nl-prayer">
                  <PrayerText html={n.prayer_requests_html} text={n.prayer_requests} />
                </div>
              ) : (
                <span className="subtle small">None recorded</span>
              )}
            </div>
          )}

          {expandFields && (
            <div className="nl-detail-section">
              <div className="nl-detail-label">Record fields</div>
              <dl className="kv" style={{ maxWidth: 420 }}>
                <dt>Date</dt>
                <dd>{n.published_on}</dd>
                <dt>Country on update</dt>
                <dd>{n.country_name || '—'}</dd>
                <dt>Highlighted need</dt>
                <dd>{n.highlighted_need ? 'On' : 'Off'}</dd>
                <dt>Word count</dt>
                <dd>{fmtInt(n.word_count)}</dd>
                <dt>Record id</dt>
                <dd className="mono">{n.id}</dd>
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function NewslettersByPartner({ user }) {
  const code = useCode()
  const canBrowse = useCanBrowse()
  const [params, setParams] = useSearchParams()
  const partnerId = params.get('partner') ? Number(params.get('partner')) : null

  const [openIds, setOpenIds] = useState(() => new Set())
  const [expandAll, setExpandAll] = useState(false)
  const [expandFields, setExpandFields] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [q, setQ] = useState('')
  // These two narrow the strip of partner units below, not the updates of the one being
  // read — they are a way of finding a unit, which is what the strip is for.
  const [countryIds, setCountryIds] = useState([])
  const [yearFrom, setYearFrom] = useState('')
  const [yearTo, setYearTo] = useState('')
  /* Everything but the unit itself: the unit is what the report is about, and clearing
     the filters should leave the reader on the same unit with all of its updates. */
  const clearFilters = () => {
    setFrom('')
    setTo('')
    setQ('')
    setCountryIds([])
    setYearFrom('')
    setYearTo('')
  }

  const query = useApi(
    ['partner-newsletters', partnerId],
    `partners/${partnerId}/newsletters`,
    { enabled: !!partnerId }
  )

  // Reset the open set when the selected partner changes.
  useEffect(() => {
    setOpenIds(new Set())
    setExpandAll(false)
  }, [partnerId])

  const setPartner = (id) => {
    const next = new URLSearchParams(params)
    if (id) next.set('partner', String(id))
    else next.delete('partner')
    setParams(next, { replace: true })
  }

  const rows = useMemo(() => {
    let list = query.data?.newsletters ?? []
    if (from) list = list.filter((n) => n.published_on >= from)
    if (to) list = list.filter((n) => n.published_on <= to)
    if (q.trim()) {
      const needle = q.trim().toLowerCase()
      list = list.filter(
        (n) =>
          String(n.body_text || '').toLowerCase().includes(needle) ||
          String(n.prayer_requests || '').toLowerCase().includes(needle)
      )
    }
    return list
  }, [query.data, from, to, q])

  const toggleRow = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = (on) => {
    setExpandAll(on)
    setOpenIds(on ? new Set(rows.map((n) => n.id)) : new Set())
  }

  // Keep "expand all" honest when the filters change the visible set. Individual
  // rows stay independently collapsible afterwards, so openIds is the only source
  // of truth for what is open.
  useEffect(() => {
    if (expandAll) setOpenIds(new Set(rows.map((n) => n.id)))
  }, [expandAll, rows])

  const partner = query.data?.partner
  const isOpen = (id) => openIds.has(id)

  return (
    <PageHeader
      title="Updates by Partner Unit"
      sub="Read-only report — pick a partner unit to see everything they have sent"
      actions={
        partner ? (
          <>
          {rows.length ? (
          <button
            className="btn btn-sm"
            onClick={() =>
              downloadCsv(
                `${code(partner.s4ca_code)}-updates.csv`,
                rows,
                [
                  { key: 'published_on', label: 'Date' },
                  { key: 'country_name', label: 'Country' },
                  { key: 'word_count', label: 'Words' },
                  { key: 'highlighted_need', label: 'Highlighted' },
                  { key: 'prayer_requests', label: 'Prayer Requests' },
                  { key: 'body_text', label: 'Text' },
                ]
              )
            }
          >
            ↓ CSV
          </button>
          ) : null}
          {/* The report is read-only; this leaves it for the form, with the unit chosen. */}
          {user?.caps.create && (
            <Link className="btn btn-sm btn-primary" to={`/newsletters/new?partner=${partnerId}`}>
              + New Update
            </Link>
          )}
          </>
        ) : null
      }
    >
      <div className="col">
        {/* Folded to begin with, like every other filter panel. It used to open itself
            whenever no unit was picked, because it was the only way to pick one — but
            the tag strip below opens in exactly that case and is the faster way in, so
            all that arrangement did was stack two open panels above an empty report. */}
        <Filters
          id="newsletters-by-partner"
          label="Partner unit &amp; filters"
          active={countFilters({ from, to, q, countryIds, yearFrom, yearTo })}
          onClear={clearFilters}
        >
          <Field label="Partner Unit" required htmlFor="rep-partner">
            <PartnerSelect
              inputId="rep-partner"
              value={partnerId}
              onChange={setPartner}
              withNewsletters
              fundType="staff"
              placeholder="Search by S4CA code, name or country…"
            />
          </Field>
          <Field label="Country" hint="Narrows the partner units below" htmlFor="rep-country">
            <CountrySelect
              inputId="rep-country"
              multi
              value={countryIds}
              onChange={setCountryIds}
            />
          </Field>
          <Field label="Partner unit since" hint="Initial year range, narrows the partner units below">
            <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
              <input
                className="input"
                type="number"
                placeholder="from"
                aria-label="Partner unit since, from year"
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value)}
              />
              <input
                className="input"
                type="number"
                placeholder="to"
                aria-label="Partner unit since, to year"
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value)}
              />
            </div>
          </Field>
          <Field label="Date range" hint="Filters the updates being read">
            <DateRangeField from={from} to={to} onFrom={setFrom} onTo={setTo} />
          </Field>
          <Field label="Find in text" hint="Filters the updates being read">
            <SearchInput value={q} onChange={setQ} placeholder="Filter these updates…" />
          </Field>
          <FilterActions
            active={countFilters({ from, to, q, countryIds, yearFrom, yearTo })}
            onClear={clearFilters}
          />
          {partnerId && (
            <div className="filters-actions">
              <Switch checked={expandAll} onChange={toggleAll} label="Expand all updates" />
              <Switch
                checked={expandFields}
                onChange={setExpandFields}
                label="Show all fields when expanded"
              />
              <span className="spacer" />
              <span className="small subtle nums">
                {fmtInt(rows.length)}
                {rows.length !== (query.data?.newsletters?.length ?? 0) &&
                  ` of ${fmtInt(query.data?.newsletters?.length)}`}{' '}
                update{rows.length === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </Filters>

        <PartnerTags
          selected={partnerId}
          onPick={setPartner}
          countryIds={countryIds}
          yearFrom={yearFrom}
          yearTo={yearTo}
        />

        {!partnerId && (
          <Card>
            {/* Points at the strip rather than the picker now: the strip is open and
                immediately above this, and the picker is inside a folded panel. */}
            <Empty icon="list" title="Choose a partner unit to begin">
              Pick one from the list above. Filters &amp; a searchable box — by S4CA code, name
              or country — are under “Partner unit &amp; filters”, at the top of the page.
            </Empty>
          </Card>
        )}

        {partnerId && query.isLoading && (
          <Card>
            <Loading label="Loading updates…" />
          </Card>
        )}

        {partnerId && query.isError && (
          <Card>
            <ErrorState error={query.error} retry={query.refetch} />
          </Card>
        )}

        {partnerId && partner && (
          <div className="report-layout">
            {/* The same card the partner unit record leads with, identified: the page
                title here is the report's, not the unit's. */}
            <PartnerProfileCard partner={partner} photo={query.data.photo} identify />

            <div className="col">
              {/* Only worth saying to someone who can do something about it: the link
                  goes to the partner form, which a view-only account does not have.
                  Keyed on the picture the card actually drew, not on the designated
                  photo — a unit with images but no photo chosen shows one of them,
                  the way the record does, and a notice denying it would be wrong. */}
              {canBrowse && !query.data.photo && (
                <Notice tone="info" icon="image">
                  No photo on file for {code(partner.s4ca_code)}. One can be added from the{' '}
                  <Link to={`/partners/${partner.partner_id}/edit`}>partner unit record</Link>.
                </Notice>
              )}

              {!rows.length ? (
                <Card>
                  <Empty title="No updates match these filters" />
                </Card>
              ) : (
                <div className="nl-list">
                  <div className="nl-list-head">
                    <span />
                    <span>Date</span>
                    <span>Update</span>
                    <span className="right">Details</span>
                    <span />
                  </div>
                  {rows.map((n) => (
                    <NewsletterRow
                      key={n.id}
                      n={n}
                      open={isOpen(n.id)}
                      onToggle={() => toggleRow(n.id)}
                      expandFields={expandFields}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </PageHeader>
  )
}
